/**
 * PURPOSE: Estados visuales del Centro de Programación (FTR-0028 WG3, §21).
 * RESPONSIBILITIES: Traducir el modelo de una fila a insignias legibles y uniformes en las cuatro
 *   vistas.
 *
 *   NUNCA SOLO COLOR. Cada insignia lleva texto y, cuando aporta, un icono. Un operador que no
 *   distingue el ámbar del verde — o que imprime la vista Por Sede en blanco y negro — tiene que
 *   poder leer el estado igual. El color acompaña, no informa por su cuenta.
 * COLLABORATORS: MatchRow.jsx, las cuatro vistas.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/components/StateBadges.jsx
 */
import React from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiEdit3,
  FiLock,
  FiPauseCircle,
  FiSlash,
  FiUserX,
} from "react-icons/fi";

const TONE_CLASS = {
  slate: "border-slate-200 bg-slate-100 text-slate-600",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-rose-200 bg-rose-50 text-rose-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
};

export const Badge = ({ tone = "slate", icon: Icon = null, children, title = undefined }) => (
  <span
    title={title}
    className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-black uppercase tracking-wide ${
      TONE_CLASS[tone] || TONE_CLASS.slate
    }`}
  >
    {Icon ? <Icon className="text-xs" aria-hidden="true" /> : null}
    {children}
  </span>
);

/**
 * Insignias de una fila, en orden de importancia operativa: primero lo que impide actuar (huérfano,
 * bloqueado, conflicto), después lo que describe su avance (materializado, programado).
 */
export const buildRowBadges = (row, { conflicts = [] } = {}) => {
  const badges = [];

  if (row.isBye) badges.push({ key: "bye", tone: "slate", icon: FiPauseCircle, label: "BYE" });
  if (row.isOrphan) {
    badges.push({
      key: "orphan",
      tone: "amber",
      icon: FiSlash,
      label: "Huérfano",
      title: "La configuración actual ya no produce esta fila. Su programación se conserva.",
    });
  }
  if (row.isOfficialized) {
    badges.push({ key: "officialized", tone: "violet", icon: FiLock, label: "Oficializado" });
  } else if (row.isMaterialized) {
    badges.push({
      key: "materialized",
      tone: "green",
      icon: FiCheckCircle,
      label: "Materializado",
      title: row.matchKey ? `Partido ${row.matchKey}` : undefined,
    });
  } else if (!row.isOrphan) {
    badges.push({ key: "draft", tone: "slate", icon: FiEdit3, label: "Borrador" });
  }

  if (!row.isBye) {
    if (row.isScheduled) {
      badges.push({ key: "scheduled", tone: "blue", icon: FiClock, label: "Programado" });
    } else {
      badges.push({ key: "unscheduled", tone: "slate", icon: FiClock, label: "Sin programar" });
    }
  }

  if (row.participantsPending) {
    badges.push({
      key: "pending-participants",
      tone: "amber",
      icon: FiUserX,
      label: "Sin participantes",
      title: "El partido existe, pero el sorteo todavía no definió quién lo juega.",
    });
  }

  if (conflicts.length) {
    const hasError = conflicts.some((conflict) => conflict.severity === "error");
    badges.push({
      key: "conflict",
      tone: hasError ? "red" : "amber",
      icon: FiAlertTriangle,
      label: hasError ? "Cruce de cancha" : "Cruce de sede",
      title: hasError
        ? "Otro partido ocupa la misma cancha a la misma hora."
        : "Otro partido coincide en la sede y alguno no tiene cancha asignada.",
    });
  }

  if (row.isManual) badges.push({ key: "manual", tone: "blue", icon: FiEdit3, label: "Manual" });
  if (row.isLocked && !row.isManual) {
    badges.push({
      key: "locked",
      tone: "slate",
      icon: FiLock,
      label: "Fijado",
      title: "Una regeneración del fixture no reescribe esta fila.",
    });
  }

  return badges;
};

const RowBadges = ({ row, conflicts = [], className = "" }) => {
  const badges = buildRowBadges(row, { conflicts });
  if (!badges.length) return null;
  return (
    <span className={`flex flex-wrap items-center gap-1 ${className}`}>
      {badges.map((badge) => (
        <Badge key={badge.key} tone={badge.tone} icon={badge.icon} title={badge.title}>
          {badge.label}
        </Badge>
      ))}
    </span>
  );
};

export default RowBadges;
